<template>
  <div class="date-swiper">
    <!-- 연도 선택용 Swiper, yearState가 true일 때만 렌더링 -->
    <swiper
      v-if="yearState"
      ref="yearSwiper"
      class="year-swiper"
      :options="yearSwiperOption"
      @slideChange="yearSlideChange"
    >
      <!-- 연도 리스트를 슬라이드로 렌더링 -->
      <swiper-slide v-for="(item, index) in yearList" :key="index" class="slide">
        <p>{{ item.code }}</p>
      </swiper-slide>

      <!-- 이전/다음 버튼 커스텀 -->
      <div class="swiper-button-prev" slot="button-prev">
        <img src="@/assets/img/icon/circle_arrow_left.png" alt="prev" />
      </div>
      <div class="swiper-button-next" slot="button-next">
        <img src="@/assets/img/icon/circle_arrow_right.png" alt="next" />
      </div>
    </swiper>

    <!-- 월 선택용 Swiper, monthState가 true일 때만 렌더링 -->
    <swiper
      v-if="monthState"
      ref="monthSwiper"
      class="month-swiper"
      :options="monthSwiperOption"
    >
      <!-- 월 리스트를 슬라이드로 렌더링 -->
      <swiper-slide
        v-for="(item, index) in monthList"
        :key="index"
        class="slide"
        :class="{ active: monthActive === index }"
      >
        <a
          href="#"
          @click.prevent="monthActive = index"
          @click="monthIndexChange(String(item.code).length === 1 ? '0' + item.code : item.code)"
        >
          {{ String(item.code).length === 1 ? '0' + item.code : item.code }}
        </a>
      </swiper-slide>
    </swiper>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
import 'swiper/css/swiper.css';
import { getMonthList, getYearList } from '@/lib';
import dayjs from 'dayjs';

export default {
  name: 'DateSlider',
  props: {
    yearState: {
      type: Boolean,
      default: false, // 연도 슬라이더 활성화 여부
    },
    monthState: {
      type: Boolean,
      default: false, // 월 슬라이더 활성화 여부
    },
    firstMonth: {
      type: Number,
      default: 1, // 월 리스트 시작 월 (예: 1월)
    },
    activeYear: {
      type: String,
      default: dayjs().format('YYYY'), // 초기 선택 연도
    },
    activeMonth: {
      type: Number, // 초기 선택 월 (숫자 타입으로 변경)
      default: 1,
    },
  },
  components: {
    Swiper,
    SwiperSlide,
  },
  mounted() {
    this.init();
  },
  data() {
    return {
      yearActive: null, // 현재 선택된 연도 인덱스
      monthActive: 0, // 현재 선택된 월 인덱스
      yearSwiperOption: {
        slidesPerView: 1,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      },
      monthSwiperOption: {
        observer: true,
        observeParents: true,
        breakpoints: {
          300: { slidesPerView: 6, slidesPerColumn: 1, spaceBetween: 0 },
          500: { slidesPerView: 8, slidesPerColumn: 1, spaceBetween: 0 },
          810: { slidesPerView: 10, slidesPerColumn: 1, spaceBetween: 0 },
          980: { slidesPerView: 12, slidesPerColumn: 1, spaceBetween: 0 },
          1000: { slidesPerView: 10, slidesPerColumn: 1, spaceBetween: 0 },
        },
      },
      swiperPause: false, // (미사용 상태)
      yearList: [], // 연도 목록
      monthList: [], // 월 목록
    };
  },
  created() {
    // activeMonth가 01(문자열)일 때 02로 변경 (로직 필요 시 조정)
    // eslint-disable-next-line vue/no-mutating-props
    this.activeMonth = String(this.activeMonth) === '01' ? '02' : this.activeMonth;

    // activeMonth를 기준으로 monthActive 인덱스 계산
    if (this.activeMonth) {
      this.monthActive =
        this.firstMonth !== 1 ? this.activeMonth - this.firstMonth : this.activeMonth - 1;
    } else {
      // activeMonth 없을 때 오늘 날짜 기준으로 계산
      const currentMonth = parseInt(dayjs().format('M'));
      this.monthActive = this.firstMonth !== 1 ? currentMonth - this.firstMonth : currentMonth - 1;
    }

    this.$nextTick(() => {
      if (this.monthState) {
        // 월 슬라이더 초기 위치 지정
        this.$refs.monthSwiper.$swiper.slideTo(this.monthActive, 0);
      }
    });
  },
  methods: {
    // 연도 및 월 리스트 초기화, 슬라이더 초기 인덱스 설정
    init() {
      this.yearList = getYearList(this.$store.state.year);
      this.monthList = getMonthList(this.firstMonth);

      const swiper = this.$refs.yearSwiper;
      if (swiper) {
        if (this.activeYear) {
          const yearIndex = this.yearList.findIndex((x) => x.code === Number(this.activeYear));
          swiper.$swiper.activeIndex = yearIndex;
          this.yearActive = yearIndex;
        } else {
          // 기본은 최신 연도 선택
          swiper.$swiper.activeIndex = this.yearList.length - 1;
          this.yearActive = swiper.$swiper.activeIndex;
        }

        if (this.monthState) {
          const monthSwiper = this.$refs.monthSwiper;
          monthSwiper.$swiper.activeIndex = 0;

          // 월 리스트 길이에 따른 슬라이드 위치 조정
          if (this.monthList.length >= 7) {
            monthSwiper.$swiper.slideTo(this.monthList.length - 1, 0);
          } else {
            monthSwiper.$swiper.slideTo(0, 0);
          }
        }
      }
    },

    // 연도 슬라이드 변경 시 실행
    yearSlideChange() {
      this.yearActive = this.$refs.yearSwiper.$swiper.activeIndex;
      this.$emit('year', this.yearList[this.yearActive].code);

      // 연도 변경 시 월 슬라이드 맨 앞으로 이동 및 월 인덱스 리셋
      if (this.monthState) {
        this.$refs.monthSwiper.$swiper.activeIndex = 0;
        this.$refs.monthSwiper.$swiper.slideTo(0, 1000, false);
        this.monthActive = 0;
        this.monthIndexChange();
      }
    },

    // 월 선택 변경 처리 함수, 인덱스 조정 및 이벤트 발생
    monthIndexChange(num) {
      const monthNum = Number(num);
      const monthSwiper = this.$refs.monthSwiper;

      // 월 번호에 따라 슬라이드 위치 조절
      if (monthNum >= 7) {
        monthSwiper.$swiper.slideTo(this.monthList.length - 1, 0);
      } else if (monthNum <= 6) {
        monthSwiper.$swiper.slideTo(0, 0);
      }

      this.$emit('month', this.monthList[this.monthActive].code);
    },
  },
};
</script>
